/**
 * Authentication context for the public portal.
 * Handles patron (reader) authentication separately from staff authentication.
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";

const PATRON_TOKEN_KEY = "patron_token";
const API_BASE = "/api/portal";

// API response types
interface PatronMeResponse {
  patron: PatronInfo;
}

interface PatronLoginResponse {
  token: string;
  patron: PatronInfo;
}

interface PatronLoginErrorResponse {
  error: string;
  code: string;
}

export interface PatronInfo {
  id: string;
  barcode: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  status: string;
  expirationDate: string | null;
  fineBalance: number;
  activeLoanCount: number;
  activeReservationCount: number;
  category: {
    id: string;
    name: string;
  };
  homeLocation: {
    id: string;
    name: string;
  };
  emailNotifications: boolean;
  preferredLanguage: string;
}

interface PatronAuthState {
  patron: PatronInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface PatronAuthContextValue extends PatronAuthState {
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => void;
  refreshPatron: () => Promise<void>;
}

const PatronAuthContext = createContext<PatronAuthContextValue | null>(null);

interface PatronAuthProviderProps {
  children: ReactNode;
}

/**
 * Get the stored patron token.
 */
function getPatronToken(): string | null {
  return localStorage.getItem(PATRON_TOKEN_KEY);
}

/**
 * Store the patron token.
 */
function setPatronToken(token: string): void {
  localStorage.setItem(PATRON_TOKEN_KEY, token);
}

/**
 * Remove the patron token.
 */
function removePatronToken(): void {
  localStorage.removeItem(PATRON_TOKEN_KEY);
}

/**
 * Check if a patron token exists.
 */
export function isPatronAuthenticated(): boolean {
  return getPatronToken() !== null;
}

/**
 * Make an authenticated API request for the portal.
 */
export async function portalFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const token = getPatronToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  headers.set("Content-Type", "application/json");

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 401 - token expired
  if (response.status === 401 && token) {
    removePatronToken();
    window.dispatchEvent(new CustomEvent("patron:unauthorized"));
  }

  return response;
}

export function PatronAuthProvider({ children }: PatronAuthProviderProps) {
  const [state, setState] = useState<PatronAuthState>({
    patron: null,
    isLoading: true,
    isAuthenticated: false,
  });

  /**
   * Fetch the current patron from the API.
   */
  const fetchPatron = useCallback(async () => {
    try {
      const response = await portalFetch("/me");
      if (!response.ok) {
        throw new Error("Failed to fetch patron");
      }
      const data = (await response.json()) as PatronMeResponse;
      setState({
        patron: data.patron,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch {
      removePatronToken();
      setState({
        patron: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  }, []);

  /**
   * Login with identifier (barcode or email) and password.
   */
  const login = useCallback(async (identifier: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    const response = await fetch(`${API_BASE}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ identifier, password }),
    });

    if (!response.ok) {
      const errorData = (await response.json()) as PatronLoginErrorResponse;
      setState({
        patron: null,
        isLoading: false,
        isAuthenticated: false,
      });
      throw new Error(errorData.error || "Échec de la connexion");
    }

    const data = (await response.json()) as PatronLoginResponse;
    setPatronToken(data.token);

    setState({
      patron: data.patron,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  /**
   * Logout the current patron.
   */
  const logout = useCallback(() => {
    removePatronToken();
    setState({
      patron: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  /**
   * Refresh patron data from the API.
   */
  const refreshPatron = useCallback(async () => {
    if (!isPatronAuthenticated()) {
      return;
    }
    await fetchPatron();
  }, [fetchPatron]);

  // Initialize auth state on mount
  useEffect(() => {
    if (isPatronAuthenticated()) {
      void fetchPatron();
    } else {
      setState({
        patron: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  }, [fetchPatron]);

  // Listen for auth events
  useEffect(() => {
    const handleUnauthorized = () => {
      setState({
        patron: null,
        isLoading: false,
        isAuthenticated: false,
      });
    };

    window.addEventListener("patron:unauthorized", handleUnauthorized);

    return () => {
      window.removeEventListener("patron:unauthorized", handleUnauthorized);
    };
  }, []);

  const value: PatronAuthContextValue = {
    ...state,
    login,
    logout,
    refreshPatron,
  };

  return <PatronAuthContext.Provider value={value}>{children}</PatronAuthContext.Provider>;
}

/**
 * Hook to access the patron auth context.
 * Must be used within a PatronAuthProvider.
 */
export function usePatronAuth(): PatronAuthContextValue {
  const context = useContext(PatronAuthContext);

  if (!context) {
    throw new Error("usePatronAuth must be used within a PatronAuthProvider");
  }

  return context;
}
